import { z } from 'zod/v4';

/**
 * Shared by the `menu` and `navigation` widgets — the app itself does NOT share these as types
 * (each widget's `type/*.ts` re-declares `MenuItem`/`SubmenuItem`/`HoverEffects` byte-for-byte,
 * `navigation-widget.type.ts` importing only `SubmenuItem` from menu's module, incidentally), but
 * duplicating a ~10-field shape twice in this package buys nothing the app's own copy-paste already
 * doesn't, and one schema means one place to fix if either widget's link/hover shape ever changes.
 *
 * `linkType` is intentionally declared here rather than reused from the button widget's own
 * (structurally identical) `buttonLinkTypeSchema` — that one is documented as kept local to button,
 * not a shared "link kind" concept, and menu/navigation's `MenuItem` is its own type in the host app
 * with no relation to a button's `link`.
 */
export const menuLinkTypeSchema = z.enum(['email', 'phone', 'link', 'anchor']);

/**
 * A submenu entry — one level of nesting under a top-level `MenuItem`, and the shape has no further
 * `submenuList` of its own (the host type does not nest a third level).
 */
export const subMenuItemSchemaLegacy = z.object({
    name: z.string(),
    linkUrl: z.string(),
    linkType: menuLinkTypeSchema,
    isVisible: z.boolean(),
    isTargetBlank: z.boolean().nullish(),
    pageId: z.string().nullish(),
    blockId: z.string().nullish(),
    id: z.string().nullish(),
});

/**
 * A top-level menu entry. `id` is nullish because it is server-generated: `menu.model.ts`'s
 * `dataAdapter` backfills a cuid2 onto any item missing one, so persisted items created before that
 * backfill (or authored without one) are still valid.
 */
export const menuItemSchemaLegacy = subMenuItemSchemaLegacy.extend({
    submenuList: z.array(subMenuItemSchemaLegacy).nullish(),
});

/**
 * Hover effect settings shared by `menu.hoverEffects` and `navigation.hoverEffects`.
 *
 * `type` is `EffectTypes` in the host app
 * (`apps/websites/src/app/shared/components/web-hover-effects/types/index.ts`) — a NUMERIC TS enum
 * (`transparency=0, color=1, underline=2, animation=3`), because that is what is actually persisted.
 * Kept numeric here, at the entity layer, so this schema parses real data byte-for-byte; the
 * AI-facing schemas in `@sites/ai-connector` expose the four names as a string union instead and
 * fold the name back to its index — a model has no reason to know these positions.
 */
export const hoverEffectsSchemaLegacy = z.object({
    type: z.number().int().min(0).max(3),
    color: z.string().nullish(),
    transparency: z.number().nullish(),
    lineType: z.string().nullish(),
    lineWidth: z.number().nullish(),
    animationType: z.string().nullish(),
});

export type SubMenuItemLegacy = z.infer<typeof subMenuItemSchemaLegacy>;
export type MenuItemLegacy = z.infer<typeof menuItemSchemaLegacy>;
export type HoverEffectsLegacy = z.infer<typeof hoverEffectsSchemaLegacy>;
